import * as React from "react";
import { useMemo } from "react";

import { Card, CardAction, CardHeader } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "../../../components/Strategies/StrategyPineSectionCard";
import { useTradePolicyFilterCatalogFields } from "@/hooks/useTradePolicyFilterCatalogFields";
import { cn } from "@/lib/utils";
import { buildPolicyViewNaturalLanguageSummary } from "../ExecutionRulesSimulator/policyTreeUtils";
import { PolicySimulatorViewer } from "../ExecutionRulesSimulator/PolicySimulatorViewer";
import { PolicyViewSummaryContent } from "../Strategies/PolicyViewSummaryContent";
import { strategiesMainCardClassName } from "../Strategies/strategiesCardShell";
import { REPORT_DETAIL_CARD_BORDER } from "../Reports/components/ReportsCardChrome";
import { WORKSPACE_PINE_DOCK_PANEL_GAP_PX } from "./workspaceDrawerWidth";
import { WorkspaceDrawerCloseButton } from "./WorkspaceDrawerActionRow";
import { WORKSPACE_PINE_DOCK_HEIGHT_PX } from "./WorkspacePineDock";
import type { WorkspaceSimulationPolicyPreviewState } from "./useWorkspaceSimulationPolicyPreview";

const PANEL_WIDTH = `calc((100% - ${WORKSPACE_PINE_DOCK_PANEL_GAP_PX}px) / 2)`;
/** Taller than pine cards so summary + written rules both fit comfortably. */
export const WORKSPACE_SIMULATION_POLICY_DOCK_HEIGHT_PX = Math.max(
  WORKSPACE_PINE_DOCK_HEIGHT_PX,
  360
);

export type WorkspaceSimulationPolicyPreviewDockProps = {
  preview: WorkspaceSimulationPolicyPreviewState;
};

/** Half-width left dock card for a simulation's policy summary (Show policy). */
export function WorkspaceSimulationPolicyPreviewDock({
  preview,
}: WorkspaceSimulationPolicyPreviewDockProps): React.ReactElement | null {
  const catalog = useTradePolicyFilterCatalogFields(preview.open);
  const summaryDocument = useMemo(() => {
    if (!preview.open || catalog.catalogLoading || catalog.catalogError) return null;
    return buildPolicyViewNaturalLanguageSummary(preview.policy, catalog.fieldsByPath);
  }, [
    catalog.catalogError,
    catalog.catalogLoading,
    catalog.fieldsByPath,
    preview.open,
    preview.policy,
  ]);

  if (!preview.open) return null;

  return (
    <div
      data-workspace-pine-dock=""
      data-workspace-pine-dock-panel-count={1}
      data-workspace-simulation-policy-preview=""
      className="pointer-events-auto relative h-full min-h-0 w-full overflow-hidden"
      style={{ height: WORKSPACE_SIMULATION_POLICY_DOCK_HEIGHT_PX }}
    >
      <div
        data-workspace-pine-dock-slot=""
        data-workspace-pine-dock-slot-position="left"
        className="absolute inset-y-0 left-0 flex min-h-0 min-w-0 flex-col overflow-hidden"
        style={{ width: PANEL_WIDTH }}
      >
        <div
          data-workspace-pine-panel=""
          data-workspace-simulation-policy-panel=""
          className="flex h-full min-h-0 flex-1 flex-col overflow-hidden"
        >
          <Card
            data-slot="card"
            data-strategy-policy-pine-card=""
            data-simulation-policy-card=""
            className="flex h-full min-h-0 flex-col gap-0 rounded-md border bg-card py-0 text-card-foreground shadow-none ring-0"
            style={{ borderColor: REPORT_DETAIL_CARD_BORDER }}
          >
            <CardHeader className="grid-cols-[1fr_auto] shrink-0 items-start gap-3 px-3 py-3 has-data-[slot=card-action]:grid-cols-[1fr_auto]">
              <div className="flex min-w-0 flex-col gap-1 text-left">
                <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Policy</FieldLabel>
                <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                  {preview.title
                    ? `Rules used for ${preview.title}.`
                    : "Rules used for this simulation."}
                </FieldDescription>
              </div>
              <CardAction className="col-start-2 row-start-1 justify-self-end">
                <div
                  data-pine-script-actions=""
                  className="flex flex-wrap items-center justify-end gap-2"
                >
                  <WorkspaceDrawerCloseButton
                    onClose={preview.hide}
                    aria-label="Hide policy"
                  />
                </div>
              </CardAction>
            </CardHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden px-3 pb-3">
              <div
                data-policy-view-summary=""
                data-simulation-policy-summary=""
                className={cn(strategiesMainCardClassName, "shrink-0 gap-1.5 border-solid p-3")}
              >
                <FieldLabel className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "m-0")}>
                  Summary
                </FieldLabel>
                <Field className="gap-0">
                  {catalog.catalogLoading ? (
                    <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                      Loading policy summary…
                    </FieldDescription>
                  ) : catalog.catalogError ? (
                    <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                      Could not load field labels for this summary.
                    </FieldDescription>
                  ) : summaryDocument ? (
                    <div data-slot="field-description" className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                      <PolicyViewSummaryContent document={summaryDocument} />
                    </div>
                  ) : (
                    <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                      No policy summary available for this simulation.
                    </FieldDescription>
                  )}
                </Field>
              </div>

              <div
                data-simulation-policy-rules=""
                data-policy-view-body=""
                className="min-w-0 shrink-0"
              >
                <PolicySimulatorViewer
                  policyTree={preview.policy}
                  versionLabel={preview.title.trim() || "version"}
                  catalog={catalog}
                  segmentChrome
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
